"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type ComponentProps,
} from "react";
import type {
  AiImagesHistoryResponse,
  AiImagesRevertResponse,
  AiImagesRunDetail,
  RunLogRow,
} from "@/types/aiImages";
import type { RevertVerdict } from "@repo/utils/aiImagesLog";
import {
  AdminBadge,
  AdminButton,
  AdminButtonSmall,
  AdminNotice,
  AdminPanel,
} from "./AdminUi";

const ACTION_LABEL: Record<string, string> = {
  apply: "Applied",
  remove: "Removed",
};

type BadgeVariant = ComponentProps<typeof AdminBadge>["variant"];

const VERDICT: Record<RevertVerdict, { label: string; variant: BadgeVariant }> =
  {
    restore: { label: "will revert", variant: "confirm" },
    unchanged: { label: "already back", variant: "muted" },
    conflict: { label: "skipped — edited since", variant: "attention" },
    missing: { label: "asset deleted", variant: "danger" },
  };

/** Empty alt is the common case here, and rendering nothing would read as a bug. */
const Alt = ({ value }: { value: string | null }) =>
  value === null ? (
    <span className="text-[var(--admin-text-faint)]">—</span>
  ) : value === "" ? (
    <span className="text-[var(--admin-text-faint)]">(empty)</span>
  ) : (
    <span className="font-mono text-xs">{value}</span>
  );

/**
 * Recent bulk runs, with a revert per run.
 *
 * Exists because Storyblok keeps no prior-alt snapshot: without this the only record that
 * a run happened is the assets it changed, and the value it overwrote is unrecoverable.
 */
const AiImagesHistory = ({ onReverted }: { onReverted: () => void }) => {
  const [runs, setRuns] = useState<RunLogRow[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState<number | null>(null);
  const [result, setResult] = useState<AiImagesRevertResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AiImagesRunDetail | null>(null);
  const [detailFor, setDetailFor] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/ai-images/history", {
        cache: "no-store",
      });

      if (!response.ok) throw new Error(`History failed (${response.status})`);

      const body = (await response.json()) as AiImagesHistoryResponse;
      setRuns(body.runs);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Failed to load history",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const toggleDetail = async (row: RunLogRow) => {
    if (detailFor === row.storyId) {
      setDetailFor(null);
      setDetail(null);
      return;
    }

    setDetailFor(row.storyId);
    setDetail(null);
    setDetailLoading(true);

    try {
      const response = await fetch(
        `/api/ai-images/history?storyId=${row.storyId}`,
        { cache: "no-store" },
      );

      if (!response.ok) throw new Error(`Detail failed (${response.status})`);

      setDetail((await response.json()) as AiImagesRunDetail);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to load run");
    } finally {
      setDetailLoading(false);
    }
  };

  const revert = async (row: RunLogRow) => {
    setReverting(row.storyId);
    setResult(null);
    setError(null);

    try {
      const response = await fetch("/api/ai-images/history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storyId: row.storyId }),
      });

      const body = (await response.json()) as AiImagesRevertResponse & {
        error?: string;
      };

      if (!response.ok && response.status !== 207) {
        setError(body.error ?? `Revert failed (${response.status})`);
        return;
      }

      setResult(body);
      onReverted();

      // Re-read the open run: every row's verdict has just changed, and leaving the old
      // preview on screen would claim a revert is still pending.
      if (detailFor === row.storyId) {
        const refreshed = await fetch(
          `/api/ai-images/history?storyId=${row.storyId}`,
          { cache: "no-store" },
        );

        if (refreshed.ok) {
          setDetail((await refreshed.json()) as AiImagesRunDetail);
        }
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Revert failed");
    } finally {
      setReverting(null);
    }
  };

  return (
    <div className="mb-4">
      <AdminButton
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        {open ? "Hide history" : "History"}
      </AdminButton>

      {open && (
        <AdminPanel className="mt-3">
          <p className="mb-3 text-sm text-[var(--admin-text-muted)]">
            Every bulk run records the alt text it overwrote, so it can be put
            back. Storyblok itself keeps no such snapshot.
          </p>

          {error && (
            <AdminNotice tone="danger" className="mb-3 p-2">
              {error}
            </AdminNotice>
          )}

          {result && (
            <AdminNotice tone="confirm" className="mb-3 p-2">
              <strong>{result.restored.length} restored</strong>
              {result.unchanged.length > 0 &&
                `, ${result.unchanged.length} already back`}
              {result.failed.length > 0 && `, ${result.failed.length} failed`}
              {result.registryError && (
                <>
                  {" "}
                  <strong>
                    The registry was not rewritten ({result.registryError}) —
                    the alt text is back, but badges still render from the old
                    registry.
                  </strong>
                </>
              )}
              {result.conflicts.length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {result.conflicts.map((conflict) => (
                    <li key={conflict.id}>
                      {/* Named individually: a skipped asset is a human edit this revert
                          deliberately refused to overwrite, not a silent failure. */}
                      <span className="font-mono text-xs">
                        {conflict.filename || conflict.id}
                      </span>{" "}
                      — {conflict.reason}
                    </li>
                  ))}
                </ul>
              )}
            </AdminNotice>
          )}

          {loading && (
            <p className="text-sm text-[var(--admin-text-muted)]">Loading…</p>
          )}

          {!loading && runs.length === 0 && (
            <p className="text-sm text-[var(--admin-text-muted)]">
              No runs recorded yet. The next apply or remove will appear here.
            </p>
          )}

          {runs.length > 0 && (
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--admin-border-strong)] text-xs tracking-wide text-[var(--admin-text-muted)] uppercase">
                  <th className="p-2">When</th>
                  <th className="p-2">Action</th>
                  <th className="p-2">Marker</th>
                  <th className="p-2">Assets</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {runs.map((row) => (
                  <Fragment key={row.storyId}>
                    <tr className="border-b border-[var(--admin-border)]">
                      <td className="p-2 font-mono text-xs">
                        {new Date(row.performedAt).toLocaleString()}
                      </td>
                      <td className="p-2">
                        {ACTION_LABEL[row.action] ?? row.action}
                      </td>
                      <td className="p-2">{row.kind || "—"}</td>
                      <td className="p-2 tabular-nums">{row.changed}</td>
                      <td className="flex gap-2 p-2">
                        <AdminButtonSmall
                          onClick={() => void toggleDetail(row)}
                          aria-expanded={detailFor === row.storyId}
                        >
                          {detailFor === row.storyId ? "Hide images" : "Images"}
                        </AdminButtonSmall>
                        <AdminButtonSmall
                          onClick={() => void revert(row)}
                          disabled={reverting === row.storyId}
                        >
                          {reverting === row.storyId ? "Reverting…" : "Revert"}
                        </AdminButtonSmall>
                      </td>
                    </tr>

                    {detailFor === row.storyId && (
                      <tr className="border-b border-[var(--admin-border)] bg-[var(--admin-surface-muted)]">
                        <td colSpan={5} className="p-2">
                          {detailLoading && (
                            <p className="text-sm text-[var(--admin-text-muted)]">
                              Reading the run…
                            </p>
                          )}

                          {detail && (
                            <>
                              <p className="mb-2 text-xs text-[var(--admin-text-muted)]">
                                Checked against the assets as they are now, so
                                this is what a revert would actually do.
                              </p>
                              <table className="w-full border-collapse text-left text-xs">
                                <thead>
                                  <tr className="tracking-wide text-[var(--admin-text-muted)] uppercase">
                                    <th className="p-1">Image</th>
                                    <th className="p-1">Before the run</th>
                                    <th className="p-1">The run set</th>
                                    <th className="p-1">Now</th>
                                    <th className="p-1">Revert</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.rows.map((entry) => (
                                    <tr
                                      key={entry.id}
                                      className="border-t border-[var(--admin-border)]"
                                    >
                                      <td className="p-1 font-mono">
                                        {entry.filename || entry.id}
                                      </td>
                                      <td className="p-1">
                                        <Alt value={entry.before} />
                                      </td>
                                      <td className="p-1">
                                        <Alt value={entry.after} />
                                      </td>
                                      <td className="p-1">
                                        <Alt value={entry.current} />
                                      </td>
                                      <td className="p-1">
                                        <AdminBadge
                                          variant={
                                            VERDICT[entry.verdict].variant
                                          }
                                        >
                                          {VERDICT[entry.verdict].label}
                                        </AdminBadge>
                                        {entry.verdict === "restore" && (
                                          <>
                                            {" → "}
                                            <Alt value={entry.before} />
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </AdminPanel>
      )}
    </div>
  );
};

export default AiImagesHistory;
